import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LinkIcon from '@mui/icons-material/Link';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useSelector } from 'react-redux';
import { axiosPrivate } from '@api/ApiHelper/axiosPrivate';
import { RootReducer } from '@store/index';
import { TwilioTemplate, isTwilioManagedTemplate } from '@screens/Notifications/WhatsappTemplateList';
import { showIOSAlert } from '@utils/Alerts/Confirm';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import { hideLoader, showLoader } from '@utils/Loader';

/**
 * Los WhatsApp que salen del sistema, con el texto REAL que ve el cliente.
 *
 * Es la ventana que no existía: para saber qué le llega a un cliente había que
 * entrar al panel de Twilio. El texto y el botón se leen en vivo desde la
 * Content API, así que esto no puede quedar desactualizado respecto a lo que de
 * verdad se manda.
 *
 * Aquí **sí se puede cambiar QUÉ plantilla usa cada aviso**, sin deploy: el
 * `contentSid` vive en Firestore (`message-flows`) y el del código queda de
 * fallback. Lo que NO se puede es editar el TEXTO — una plantilla aprobada por
 * Meta es inmutable, así que para cambiar palabras se crea otra en el tab de
 * Plantillas y se vincula cuando la aprueben.
 *
 * ⚠️ **El selector solo ofrece plantillas APROBADAS y con la MISMA cantidad de
 * variables** que el aviso manda. No es cosmético: con el número equivocado el
 * mensaje no se envía a medias, **se cancela**, y el error muere en un
 * `console.error`. El server valida lo mismo por su cuenta.
 */

interface WhatsappFlow {
    key: string;
    name: string;
    description: string;
    contentSid: string;
    variables: string[];
    friendlyName?: string;
    body?: string;
    actions?: { title?: string; url?: string }[];
    twilioVariables?: number;
    unreadable?: boolean;
    /** true = alguien la cambió desde aquí; false = la que trae el código. */
    isCustom?: boolean;
    /** Interruptores (`message-flows/{key}`). Ausente = encendido. */
    enabled?: boolean;
    posEnabled?: boolean;
    /** El flujo también se dispara por ventas del POS → se le muestra el interruptor fino. */
    appliesToPos?: boolean;
}

const WhatsappTemplates: React.FunctionComponent = () => {
    const { loggedUser } = useSelector((state: RootReducer) => state.auth);
    const [flows, setFlows] = useState<WhatsappFlow[]>([]);
    const [aprobadas, setAprobadas] = useState<TwilioTemplate[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');

    const cargar = useCallback(async () => {
        try {
            const [flujos, todas] = await Promise.all([
                axiosPrivate.get('/twilio/templates'),
                axiosPrivate.get('/twilio/templates/all')
            ]);
            setFlows(flujos.data?.flows || []);
            // Solo se ofrecen las APROBADAS: vincular una en revisión haría
            // fallar los envíos, y el server además lo rechaza.
            setAprobadas(
                (todas.data?.templates || []).filter(
                    (t: TwilioTemplate) =>
                        // Aprobadas Y nuestras: las 71 que Twilio creó para el
                        // código OTP tienen 1 variable igual que el aviso de
                        // reseña, y aparecerían como opciones válidas para él.
                        t.status === 'approved' && !isTwilioManagedTemplate(t.friendlyName)
                )
            );
        } catch (err) {
            setError('No se pudieron leer las plantillas desde Twilio.');
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        void cargar();
    }, [cargar]);

    const vincular = async (flow: WhatsappFlow, contentSid: string) => {
        showLoader();
        try {
            await axiosPrivate.put(`/twilio/flows/${flow.key}`, {
                contentSid,
                updatedBy: loggedUser?.profile?.name
            });
            showAlertMessage('Aviso revinculado');
            await cargar();
        } catch (err: any) {
            // El server valida la ARIDAD y la aprobación: su mensaje explica
            // exactamente por qué no se puede, y eso es lo que hay que mostrar.
            showAlertError(err?.response?.data?.error || err);
        }
        hideLoader();
    };

    /**
     * Encender o apagar un aviso. El server lo lee en cada envío (caché de 60 s),
     * así que el cambio surte efecto en menos de un minuto y sin deploy.
     *
     * El interruptor del POS existe porque el "compra confirmada" de cada venta
     * de mostrador era el 85 % de los WhatsApp que se pagan (medido el
     * 2026-09-14); apagarlo deja intacto el aviso de los pedidos de la app.
     */
    const cambiarInterruptor = async (flow: WhatsappFlow, cambio: { enabled?: boolean; posEnabled?: boolean }) => {
        showLoader();
        try {
            await axiosPrivate.put(`/twilio/flows/${flow.key}`, { ...cambio, updatedBy: loggedUser?.profile?.name });
            // Se refleja al instante sin releer Twilio: `cargar` tarda porque lee
            // el texto de cada plantilla desde la Content API.
            setFlows((prev) => prev.map((f) => (f.key === flow.key ? { ...f, ...cambio } : f)));
            const apagado = cambio.enabled === false || cambio.posEnabled === false;
            showAlertMessage(apagado ? 'Aviso apagado: deja de salir en menos de un minuto' : 'Aviso encendido');
        } catch (err: any) {
            showAlertError(err?.response?.data?.error || err);
        }
        hideLoader();
    };

    const restaurar = (flow: WhatsappFlow) => {
        showIOSAlert({
            title: 'Volver a la original',
            message: `«${flow.name}» volverá a usar la plantilla con la que salió el sistema.`,
            buttons: [
                { text: 'Restaurar', onPress: () => vincular(flow, '') },
                { text: 'Cancelar', onPress: () => {}, style: 'cancel' }
            ]
        });
    };

    if (loading) {
        return (
            <Stack height="100%" alignItems="center" justifyContent="center">
                <CircularProgress />
            </Stack>
        );
    }

    return (
        <Box height="100%" p={4} sx={{ overflowY: 'auto' }}>
            <Stack spacing={2} maxWidth={860}>
                <Stack>
                    <Typography variant="h5" fontWeight={600}>
                        Mensajes de WhatsApp
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Lo que el sistema manda por WhatsApp, con el texto tal como lo recibe el cliente.
                    </Typography>
                </Stack>

                <Alert severity="info">
                    Al cliente le llega <strong>un solo WhatsApp por pedido</strong> —el de compra— porque cada
                    mensaje se cobra. Ese aviso sale también en <strong>cada venta del POS</strong> con teléfono de
                    cliente, y es la mayor parte de lo que se paga: aquí se puede apagar solo para el POS sin tocar
                    el de la app. Los pedidos de <strong>delivery</strong> además avisan que van en camino. Todo lo
                    demás vive en las notificaciones, que no cuestan por envío.
                </Alert>

                {error && <Alert severity="warning">{error}</Alert>}

                {flows.map((flow) => (
                    <Stack key={flow.key} component={Card} boxShadow={2} borderRadius={3} p={3} spacing={1.5}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <WhatsAppIcon color="success" />
                            <Typography variant="subtitle1" fontWeight={600}>
                                {flow.name}
                            </Typography>
                            {flow.enabled === false && <Chip label="Apagado" size="small" />}
                            {flow.enabled !== false && flow.appliesToPos && flow.posEnabled === false && (
                                <Chip label="Sin ventas del POS" size="small" variant="outlined" />
                            )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            {flow.description}
                        </Typography>

                        {/* Interruptores: viven en `message-flows/{key}` (ausente =
                            encendido) y el server los consulta en cada envío. */}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 3 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={flow.enabled !== false}
                                        onChange={(_, checked) => cambiarInterruptor(flow, { enabled: checked })}
                                    />
                                }
                                label="Enviar este aviso"
                            />
                            {flow.appliesToPos && (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={flow.posEnabled !== false}
                                            disabled={flow.enabled === false}
                                            onChange={(_, checked) => cambiarInterruptor(flow, { posEnabled: checked })}
                                        />
                                    }
                                    label="También en ventas de mostrador (POS)"
                                />
                            )}
                        </Stack>
                        {flow.appliesToPos && (
                            <Typography variant="caption" color="text.secondary">
                                Cada venta del POS con teléfono de cliente manda este WhatsApp, y cada uno se cobra. Con el
                                interruptor del POS apagado solo sale en pedidos de la app (web, kiosco y mesa); el botón
                                «Reenviar» de un pedido lo manda de todos modos.
                            </Typography>
                        )}

                        {flow.unreadable ? (
                            <Alert severity="warning">
                                No se pudo leer el texto desde Twilio. El mensaje se sigue enviando igual.
                            </Alert>
                        ) : (
                            <>
                                <Divider />
                                {/* `pre-line` porque el cuerpo de la plantilla trae
                                    saltos de línea reales y aquí importan: es
                                    exactamente lo que ve el cliente. */}
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                    {flow.body}
                                </Typography>
                                {flow.actions?.map((action, index) => (
                                    <Stack key={index} direction="row" alignItems="center" spacing={0.5}>
                                        <LinkIcon fontSize="small" color="action" />
                                        <Typography variant="caption" color="text.secondary">
                                            Botón «{action.title}» → {action.url}
                                        </Typography>
                                    </Stack>
                                ))}
                            </>
                        )}

                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {flow.variables.map((variable, index) => (
                                <Chip key={variable} label={`{{${index + 1}}} ${variable}`} size="small" />
                            ))}
                        </Stack>

                        <Divider />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                            <TextField
                                select
                                size="small"
                                label="Plantilla que usa este aviso"
                                value={flow.contentSid || ''}
                                onChange={(event) => vincular(flow, event.target.value)}
                                fullWidth
                                helperText={`Solo se ofrecen las aprobadas con ${flow.variables.length} variable(s)`}
                            >
                                {/* Se filtra por aridad ANTES de ofrecerla: que ni
                                    siquiera se pueda elegir una que dejaría mudo
                                    el aviso. El server lo valida otra vez. */}
                                {aprobadas
                                    .filter((t) => t.variableCount === flow.variables.length)
                                    .map((t) => (
                                        <MenuItem key={t.sid} value={t.sid}>
                                            {t.friendlyName}
                                        </MenuItem>
                                    ))}
                                {flow.contentSid && !aprobadas.some((t) => t.sid === flow.contentSid) && (
                                    <MenuItem value={flow.contentSid}>
                                        {flow.friendlyName || flow.contentSid} (actual)
                                    </MenuItem>
                                )}
                            </TextField>
                            {flow.isCustom && (
                                <Button size="small" startIcon={<RestartAltIcon />} onClick={() => restaurar(flow)}>
                                    Original
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                ))}

                <Alert severity="warning">
                    El texto de una plantilla <strong>no se puede editar</strong> —una vez aprobada por Meta es
                    inmutable—, pero sí puedes <strong>crear una nueva</strong> en el tab de al lado y, cuando la
                    aprueben, vincularla aquí.
                </Alert>
            </Stack>
        </Box>
    );
};

export default WhatsappTemplates;
